import axios from "axios";
import { BASE_URL } from "./config";
import { v4 as uuidv4 } from "uuid";
import SessionManager from "./session-manager";
import { clearClientSession, signOutIsInFlight } from "./session";
import { toast } from "react-toastify";

const instance = axios.create({
  baseURL: BASE_URL,
});

// Requests that *establish* a session rather than use one. A 401 here means
// "sign-in did not succeed", not "your session expired" — auto-logging out
// would wipe the tokens the auth callback just obtained and strand the user
// on a blank page with no error. The caller surfaces these instead.
// Note baseURL already contains /api, so config.url is the bare path.
const SESSION_BOOTSTRAP_PATHS = [
  "/login/refresh-token",
  "/users/getUser",
];

const isSessionBootstrap = (url?: string) =>
  !!url && SESSION_BOOTSTRAP_PATHS.some((path) => url.includes(path));

instance.interceptors.request.use(
  async (config) => {
    const sessionManager = new SessionManager();
    const token = await sessionManager.getAccessToken();
    const headers = config.headers as any;

    if (config.data instanceof FormData) {
      if (typeof headers?.delete === "function") {
        headers.delete("Content-Type");
      } else if (headers) {
        delete headers["Content-Type"];
      }
    } else if (!headers?.["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    config.headers["correlation-id"] = uuidv4();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  async (res) => {
    const sessionManager = new SessionManager();
    await sessionManager.getRefreshToken();
    return res;
  },
  async (err) => {
    const originalConfig = err.config;
    if (originalConfig?.url && !isSessionBootstrap(originalConfig.url)) {
      // Optional chaining: a network error (backend down) has no response at
      // all, and reading .status on it threw inside the interceptor.
      if (err.response?.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;
        if (signOutIsInFlight()) {
          // A deliberate logout already owns the teardown and the single
          // navigation to the Cognito hosted logout. The dashboards poll on a
          // timer, so a 401 lands here routinely mid-logout; clearing storage
          // or hard-navigating now would abort that uncommitted navigation and
          // leave the hosted session alive — the user would look signed in
          // again and have to log out twice (ICE-BC-BUG-018).
          return Promise.reject(err);
        }
        clearClientSession();
        // Send the user to the dedicated logged-out screen, never to the
        // client-facing site at "/".
        window.location.href = "/logged-out";
        return Promise.reject(err);
        // try {
        //   const sessionManager = new SessionManager();
        //   const refreshToken = await sessionManager.getRefreshToken();
        //   try {
        //     const response = await axios.post(
        //       `${BASE_URL}/login/refresh-token`,
        //       {
        //         refresh_token: refreshToken,
        //       },
        //       {
        //         headers: {
        //           "Content-Type": "application/json",
        //           "correlation-id": uuidv4(),
        //           Accept: "*/*",
        //         },
        //       }
        //     );

        //     const newAccessToken = response.data?.access_token;
        //     const newRefreshToken = response.data?.refresh_token;

        //     await sessionManager.setAccessToken(newAccessToken);
        //     await sessionManager.setRefreshToken(newRefreshToken);
        //   } catch (error) {
        //     return error;
        //   }

        //   return instance(originalConfig);
        // } catch (error: any) {
        //   if (error.response?.status === 401) {
        //     const { logout } = useAuthStore.getState();
        //     logout();
        //     useAuthStore.persist.clearStorage();
        //     localStorage.clear();
        //     window.location.reload();
        //     window.location.href = "/login";
        //   }
        //   return Promise.reject(error);
        // }
      }
    }

    // Business screens own their action feedback. Showing every API error here
    // as well as at the call site produces two toasts for the same failure.
    // Keep global error reporting opt-in for background requests that do not
    // have a screen-level error handler.
    const status = err.response?.status;
    if (status && status !== 401 && originalConfig?._showGlobalError === true) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        `Request failed (${status}). Please try again or contact support.`;
      toast.error(message, { toastId: String(message) });
    }

    return Promise.reject(err);
  }
);

export default instance;
